import { LanguageDropdown } from '@/components/LanguageDropdown';
import styles from './KioskHeader.module.css';
import { InstitutionLogoContent } from '@/components/InstitutionLogoContent';
import { Avatar, Button, Modal, Typography } from '@globalmed/atlas-ui';
import { useUsersStore } from '@/stores/users';
import { useTranslation } from 'react-i18next';
import { getUserInitials, nameFormat } from '@/utils/valuesFormat';
import { matchPath, useNavigate } from 'react-router-dom';
import { getLinkToLogin } from '@/utils/getLinkToLogin';
import { useKioskStore } from '@/stores/kiosk';
import { KioskSessionOverrideCountdown } from '../KioskSessionOverrideCountdown';
import { EKioskRouterNames } from '@/types';
import { useState } from 'react';

/**
 * Renders the header for kiosk pages.
 * Displays language selection, institution branding, and
 * information about the currently authenticated user with a logout action.
 * @returns KioskHeader component.
 */
export const KioskHeader = () => {
  const [openLogOutModal, setOpenLogOutModal] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, logout } = useUsersStore((state) => ({
    userData: state.userData,
    logout: state.logout,
  }));
  const kioskInstitution = useKioskStore((state) => state.kioskInstitution);
  const isLandingPage = matchPath(EKioskRouterNames.KIOSK, location.pathname);
  const isLogoutNoticePage = matchPath(
    `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__LOGOUT_NOTICE}`,
    location.pathname,
  );

  const displayName = kioskInstitution?.name;

  const fullName = userData
    ? nameFormat(userData.firstName, userData.lastName)
    : null;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      navigate(getLinkToLogin());
      setOpenLogOutModal(false);
    }
  };
  return (
    <header className={styles.container}>
      <LanguageDropdown className={styles.language} />
      <div className={styles.institutionInfo}>
        <InstitutionLogoContent
          whitelabel={kioskInstitution?.whitelabel}
          isPreferBanner={false}
        />
        <Typography fontSize="20px" fontWeight={500}>
          {displayName}
        </Typography>
      </div>
      <div className={styles.userInfoWrapper}>
        {userData && fullName && (
          <div className={styles.userInfo}>
            <Avatar
              size="sm"
              src={userData.meta?.profilePicture}
              alt={fullName}
              text={getUserInitials(userData.firstName, userData.lastName)}
            />
            <Typography fontSize="14px" fontWeight={500}>
              {fullName}
            </Typography>
            <Button
              preIcon="LogOut"
              variant="secondary"
              color="destructive"
              text={t('first-login.log-out')}
              onClick={() => setOpenLogOutModal(true)}
            />
          </div>
        )}
      </div>
      {!isLandingPage && !isLogoutNoticePage && (
        <KioskSessionOverrideCountdown
          text={t('kiosk.device-is-scheduled-to-be-used')}
          timerText={t('kiosk.you-will-be-logged-out-in')}
        />
      )}
      <Modal
        open={openLogOutModal}
        onOpenChange={() => setOpenLogOutModal(false)}
        title={t('kiosk.are-you-sure-you–want-to-log-out')}
        sx={{ width: '480px' }}
        body={
          <div className={styles.btnsModal}>
            <Button
              text={t('cancel')}
              variant="secondary"
              onClick={() => setOpenLogOutModal(false)}
            />
            <Button
              color="destructive"
              text={t('kiosk.yes-log-out')}
              onClick={handleLogout}
            />
          </div>
        }
      />
    </header>
  );
};
